import { AuthTokenStorageService } from '@/lib/auth';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import {
  fetchCvCompanies,
  createCvByType,
  fetchCvDetail,
  fetchCvPositions,
  fetchCvSchools,
  fetchCvSkills,
  updateCvByType,
  deleteCvByType,
  uploadTrainingCertificate,
  uploadProfileImage,
  cvExperienceEndorsement,
  cvSkillEndorsement,
  updateCvVisibleCourse,
  updateCvVisibleTestly,
  getCvReviewers,
  getJobOfferList,
  acceptJobOffer,
  type AcceptJobOfferPayload,
  updateCvVisibleExam,
} from '@/features/cv/api/cv.api';
import { fetchCvAiAbout } from '@/features/cv/api/ai.api';
import { aiQueryKeys } from '@/features/cv/api/ai.keys';
import { toast } from 'react-toastify';
import { cvQueryKeys } from '@/features/cv/api/cv.keys';
import useDebounce from '@/hooks/useDebounce';
import {
  CreateCvDataDto,
  DeleteCvDataDto,
  UpdateCvDataDto,
} from '@/shared/cv/cv.dto';
import { EsCvData } from '@/shared/cv/cv.es.dto';
import { IFilter } from '@/shared/base/base.dto';
import { setUserAvatarInCache } from '@/features/user/api';
import {
  CvSkillSearchParams,
  CvVisibleCoursePayload,
  CvVisibleExamPayload,
  CvVisibleTestlyPayload,
} from '@/types/cv';
import { getMutationErrorMessage } from '@/lib/api';
import { isCvAnket } from '@/lib/utils/cv/common';
import { useCallback } from 'react';
import {
  CvDataType,
  CvExamInputDto,
  CvInternshipInputDto,
} from '@/shared/cv/cv.dto';
import { mapExamFormToPayload } from '@/lib/utils/cv/exam';
import { mapInternshipFormToPayload } from '@/lib/utils/cv/internship';

function getCvToastMessage(mn: string, en: string): string {
  if (typeof window === 'undefined') return mn;
  return localStorage.getItem('currentLanguage') === '2' ? en : mn;
}

export const useMyCv = () => {
  const token = AuthTokenStorageService.getAccessToken();
  return useQuery({
    queryKey: cvQueryKeys.myCv,
    queryFn: fetchCvDetail,
    enabled: !!token,
  });
};

export function useCvAnket() {
  const query = useMyCv();

  return {
    ...query,
    isAnket: isCvAnket(query.data?.cvPercent),
  };
}

export const useCvSkillSearch = (
  params: CvSkillSearchParams,
  options?: { enabled?: boolean }
) => {
  const debouncedSearch = useDebounce(params.search ?? '');

  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: [
      cvQueryKeys.cvSkills,
      debouncedSearch,
      params.groupId,
      params.page,
      params.limit,
    ],
    queryFn: () =>
      fetchCvSkills({
        ...params,
        search: debouncedSearch,
      }),
    // limit/search өөрчлөгдөхөд жагсаалт spinner болж анивчихгүй (ижил бүлгийн өмнөх үр дүнг түр харуулна)
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === params.groupId ? previousData : undefined,
  });
};

export const useUpdateCv = () => {
  return useMutation({
    mutationFn: (data: UpdateCvDataDto) => updateCvByType(data),
    onSuccess: (response: EsCvData) => {
      toast.success('Амжилттай шинэчлэгдлээ');
      queryClient.setQueryData(cvQueryKeys.myCv, response);
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};

export const useCreateCv = () => {
  return useMutation({
    mutationFn: ({ type, data }: { type: string; data: CreateCvDataDto }) =>
      createCvByType(type, data),
    onSuccess: (response: EsCvData) => {
      toast.success(getCvToastMessage('Амжилттай бүртгэгдлээ', 'Successfully'));
      queryClient.setQueryData(cvQueryKeys.myCv, response);
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};

export const useDeleteCv = () => {
  return useMutation({
    mutationFn: (data: DeleteCvDataDto) => deleteCvByType(data),
    onSuccess: (response: EsCvData) => {
      toast.success('Амжилттай устгагдлаа');
      queryClient.setQueryData(cvQueryKeys.myCv, response);
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};

export const useUploadTrainingCertificate = () => {
  return useMutation({
    mutationFn: uploadTrainingCertificate,
    onSuccess: (response: EsCvData) => {
      toast.success('Сертификат амжилттай хуулагдлаа');
      if (response) {
        queryClient.setQueryData(cvQueryKeys.myCv, response);
      }
    },
    onError: (error: unknown) => {
      toast.error(
        getMutationErrorMessage(
          error,
          'Сертификат хуулахад алдаа гарлаа. Дахин оролдоно уу.'
        )
      );
    },
  });
};

export const useGetCvCompanies = (
  search?: string,
  limit?: number,
  page?: number
) => {
  const debouncedSearch = useDebounce(search ?? '');
  return useQuery({
    queryKey: [cvQueryKeys.cvCompanies, debouncedSearch, limit, page],
    queryFn: () => fetchCvCompanies(debouncedSearch, limit, page),
  });
};

export const useGetCvPositions = (query?: string, limit?: number) => {
  const debouncedQuery = useDebounce(query ?? '');
  return useQuery({
    queryKey: [cvQueryKeys.cvPositions, debouncedQuery, limit],
    queryFn: () => fetchCvPositions(debouncedQuery, limit),
  });
};

export const useGetCvSchools = (
  countryId?: number,
  categoryId?: number,
  search?: string
) => {
  const debouncedSearch = useDebounce(search ?? '');
  const normalizedCountryId = countryId ?? 0;
  const normalizedCategoryId = categoryId ?? 0;

  return useQuery({
    queryKey: [
      cvQueryKeys.cvSchools,
      normalizedCountryId,
      normalizedCategoryId,
      debouncedSearch,
    ],
    queryFn: () =>
      fetchCvSchools({
        countryId: normalizedCountryId,
        categoryId: normalizedCategoryId,
        search: debouncedSearch,
      }),
    enabled:
      normalizedCountryId > 0 &&
      normalizedCategoryId > 0 &&
      debouncedSearch.trim().length > 0,
  });
};

export const useUploadProfileImage = () => {
  return useMutation({
    mutationFn: uploadProfileImage,
    onSuccess: async (response: EsCvData) => {
      toast.success('Амжилттай шинэчлэгдлээ');
      queryClient.setQueryData(cvQueryKeys.myCv, response);
      if (response.picture) {
        setUserAvatarInCache(response.picture);
      }
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};

export const useCvExperienceEndorsement = () => {
  return useMutation({
    mutationFn: cvExperienceEndorsement,
    onSuccess: (response: EsCvData) => {
      toast.success(
        getCvToastMessage('Амжилттай шинэчлэгдлээ', 'Successfully')
      );
      queryClient.setQueryData(cvQueryKeys.myCv, response);
    },
  });
};

export const useCvSkillEndorsement = () => {
  return useMutation({
    mutationFn: cvSkillEndorsement,
    onSuccess: (response: EsCvData) => {
      toast.success(
        getCvToastMessage('Амжилттай шинэчлэгдлээ', 'Successfully')
      );
      queryClient.setQueryData(cvQueryKeys.myCv, response);
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};

export const useGenerateCvAiAbout = () => {
  return useMutation({
    mutationFn: (lang: number) => fetchCvAiAbout(lang),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiQueryKeys.cvAiUsage });
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};

export const useUpdateCvVisibleCourse = () => {
  return useMutation({
    mutationFn: (data: CvVisibleCoursePayload) => updateCvVisibleCourse(data),
    onSuccess: (response) => {
      if (response.data) {
        queryClient.setQueryData(cvQueryKeys.myCv, response.data);
      }
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};
export const useUpdateCvVisibleExam = () => {
  return useMutation({
    mutationFn: (data: CvVisibleExamPayload) => updateCvVisibleExam(data),
    onSuccess: (response) => {
      if (response.data) {
        queryClient.setQueryData(cvQueryKeys.myCv, response.data);
      }
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};

export const useUpdateCvVisibleTestly = () => {
  return useMutation({
    mutationFn: (data: CvVisibleTestlyPayload) => updateCvVisibleTestly(data),
    onSuccess: (response) => {
      if (response.data) {
        queryClient.setQueryData(cvQueryKeys.myCv, response.data);
      }
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};

export const useGetCvReviewers = (params: IFilter) => {
  const token = AuthTokenStorageService.getAccessToken();

  return useQuery({
    queryKey: cvQueryKeys.cvReviewers.list(params),
    queryFn: () => getCvReviewers(params),
    enabled: !!token,
    refetchOnWindowFocus: false,
  });
};

export const useGetJobOffers = (params: IFilter) => {
  const token = AuthTokenStorageService.getAccessToken();

  return useQuery({
    queryKey: cvQueryKeys.jobOffers.list(params),
    queryFn: () => getJobOfferList(params),
    enabled: !!token,
  });
};

export const useAcceptJobOffer = () => {
  return useMutation<EsCvData, unknown, AcceptJobOfferPayload>({
    mutationFn: (data) => acceptJobOffer(data),
    onSuccess: (response: EsCvData) => {
      toast.success('Амжилттай шинэчлэгдлээ');
      queryClient.setQueryData(cvQueryKeys.myCv, response);
    },
    onError: (error: unknown) => {
      toast.error(getMutationErrorMessage(error));
    },
  });
};

interface SaveExamOptions {
  langId: number;
  data: CvExamInputDto;
  examId?: number;
  onSuccess?: () => void;
}

interface SaveInternshipOptions {
  langId: number;
  data: CvInternshipInputDto;
  internshipId?: number;
  onSuccess?: () => void;
}

export function useDeleteTrainingItem() {
  const { mutate, isPending } = useDeleteCv();

  const deleteTraining = useCallback(
    (trainingId: number, onSuccess?: () => void) => {
      mutate(
        {
          type: CvDataType.TRAINING,
          data: { id: trainingId },
        },
        { onSuccess }
      );
    },
    [mutate]
  );

  return { deleteTraining, isDeleting: isPending };
}

export function useDeleteExamItem() {
  const { mutate, isPending } = useDeleteCv();

  const deleteExam = useCallback(
    (examId: number, onSuccess?: () => void) => {
      mutate(
        {
          type: CvDataType.EXAM,
          data: { id: examId },
        },
        { onSuccess }
      );
    },
    [mutate]
  );

  return { deleteExam, isDeleting: isPending };
}

export function useDeleteInternshipItem() {
  const { mutate, isPending } = useDeleteCv();

  const deleteInternship = useCallback(
    (internshipId: number, onSuccess?: () => void) => {
      mutate(
        {
          type: CvDataType.INTERNSHIP,
          data: { id: internshipId },
        },
        { onSuccess }
      );
    },
    [mutate]
  );

  return { deleteInternship, isDeleting: isPending };
}

export function useSaveExam() {
  const { mutate: createExam, isPending: isCreating } = useCreateCv();
  const { mutate: updateExam, isPending: isUpdating } = useUpdateCv();

  const saveExam = useCallback(
    ({ langId, data, examId, onSuccess }: SaveExamOptions) => {
      const payload = mapExamFormToPayload(data, langId, examId);

      if (examId) {
        updateExam(
          {
            type: CvDataType.EXAM,
            langId,
            data: payload,
          },
          { onSuccess: () => onSuccess?.() }
        );
        return;
      }

      createExam(
        {
          type: CvDataType.EXAM,
          data: { langId, data: payload },
        },
        { onSuccess: () => onSuccess?.() }
      );
    },
    [createExam, updateExam]
  );

  return {
    saveExam,
    isSaving: isCreating || isUpdating,
  };
}

export function useSaveInternship() {
  const { mutate: createInternship, isPending: isCreating } = useCreateCv();
  const { mutate: updateInternship, isPending: isUpdating } = useUpdateCv();

  const saveInternship = useCallback(
    ({ langId, data, internshipId, onSuccess }: SaveInternshipOptions) => {
      const payload = mapInternshipFormToPayload(data, langId, internshipId);

      if (internshipId) {
        updateInternship(
          {
            type: CvDataType.INTERNSHIP,
            langId,
            data: payload,
          },
          { onSuccess: () => onSuccess?.() }
        );
        return;
      }

      createInternship(
        {
          type: CvDataType.INTERNSHIP,
          data: { langId, data: payload },
        },
        { onSuccess: () => onSuccess?.() }
      );
    },
    [createInternship, updateInternship]
  );

  return {
    saveInternship,
    isSaving: isCreating || isUpdating,
  };
}
